/** @file Session action: Undo of an applied plan = a guarded revert of exactly its rev (spec §9). */
import type { AppliedView, SessionCore } from './autorigTypes';

/** The applied view still on screen for `rev`, or null after a close or a newer run. */
function current(core: SessionCore, rev: number): AppliedView | null {
  const now = core.store.getState();
  return now.kind === 'applied' && now.rev === rev ? now : null;
}

/**
 * Once; a REVERT_CONFLICT (a later commit touched the same paths) stays on the banner and Undo stays available.
 * The revert's rev is then awaited on the confirmed doc (W3-72); a failure of that wait goes on the banner.
 */
export async function undoApplied(core: SessionCore): Promise<void> {
  const view = core.store.getState();
  if (view.kind !== 'applied' || view.undoing || view.undone) return;
  core.store.setState({ ...view, undoing: true, failure: null }, true);
  let reverted: number;
  try {
    reverted = (await core.ports.revert(view.rev)).rev;
  } catch (thrown) {
    const now = current(core, view.rev);
    if (now !== null) core.store.setState({ ...now, undoing: false, failure: { thrown } }, true);
    return;
  }
  const done = current(core, view.rev);
  if (done !== null) core.store.setState({ ...done, undoing: false, undone: true }, true);
  try {
    await core.ports.confirmed(reverted);
  } catch (thrown) {
    const now = current(core, view.rev);
    if (now !== null) core.store.setState({ ...now, failure: { thrown } }, true);
  }
}
